import React, { useState } from "react";
import { Box, InputBase, Typography, CircularProgress, ClickAwayListener } from "@mui/material";
import { Search, ArrowRight, BookOpen, Briefcase, FileText, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { unifiedSearch } from "../../api/search";

const RESULT_META = {
  courses: { label: "Course", icon: BookOpen, color: "#2563EB", to: () => "/learn" },
  services: { label: "Service", icon: Briefcase, color: "#0891B2", to: () => "/business" },
  resources: { label: "Resource", icon: FileText, color: "#7C3AED", to: () => "/resources" },
};

// NOTE: this component now accepts `sx` (and ignores/forwards `fullWidth`) so a parent
// like Header.jsx can actually control its width. Previously the field had a hard-coded
// pixel width object ({ xs: 130, sm: 190, lg: 210 }) and the component didn't take any
// props at all — so whatever width Header tried to hand it (via a wrapping Box + sx) was
// silently ignored, and the field always rendered at a fixed 210px on desktop no matter
// how much space its container actually had. That's what was producing the empty gap
// before the "Talk To Experts" button.
export default function NavSearch({ sx, fullWidth, ...rest }) {
  const [focused, setFocused] = useState(false);
  const [q, setQ] = useState("");

  const { data, isFetching } = useQuery({
    queryKey: ["search", q],
    queryFn: () => unifiedSearch(q).then((r) => r.data),
    enabled: q.length >= 2,
  });

  const groups = [
    { key: "courses", items: data?.courses, titleKey: "title" },
    { key: "services", items: data?.services, titleKey: "name" },
    { key: "resources", items: data?.resources, titleKey: "title" },
  ].filter((g) => g.items?.length);

  const hasResults = groups.length > 0;
  const showPanel = focused && q.length >= 2;
  const totalCount = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <ClickAwayListener onClickAway={() => setFocused(false)}>
      <Box sx={{ position: "relative", minWidth: 0, width: fullWidth ? "100%" : "auto" }}>
        {/* Field — compact fixed size on mobile (acts like a small trigger), but fills
            whatever width its parent container gives it on md/lg instead of a hard 210px
            cap, so it actually stretches to match the space Header allocates for it. */}
        <Box
          {...rest}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            height: 38,
            width: { xs: 130, sm: 190, md: "100%", lg: "100%" },
            minWidth: 0,
            maxWidth: "100%",
            bgcolor: "rgba(15,23,42,0.035)",
            border: "1px solid",
            borderColor: focused ? "#2563EB" : "rgba(15,23,42,0.35)",
            borderRadius: showPanel ? "10px 10px 0 0" : "10px",
            boxShadow: focused ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
            pl: { xs: 1.1, sm: 1.1 },
            pr: { xs: 0.6, sm: 0.6 },
            justifyContent: "center",
            transition: "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
            "&:hover": { borderColor: focused ? "#2563EB" : "rgba(37,99,235,0.5)" },
            // Any sx passed in from a parent (e.g. Header) wins last, so callers can still
            // override width/margins explicitly if they need to.
            ...sx,
          }}
        >
          <Box sx={{ display: { xs: "flex", sm: "flex" }, alignItems: "center", color: focused ? "#2563EB" : "#94A3B8", flexShrink: 0 }}>
            {isFetching ? <CircularProgress size={14} thickness={5} sx={{ color: "#2563EB" }} /> : <Search size={16} strokeWidth={2.25} />}
          </Box>

          <InputBase
            fullWidth
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setFocused(true)}
            sx={{
              display: { xs: "flex", sm: "flex" },
              minWidth: 0,
              fontSize: 13,
              fontWeight: 600,
              color: "#000000",
              "& input": { minWidth: 0 },
              "& input::placeholder": { color: "#94A3B8", opacity: 1 },
            }}
          />

          {q.length > 0 && (
            <Box
              role="button"
              aria-label="Clear search"
              onClick={(e) => { e.stopPropagation(); setQ(""); }}
              sx={{
                display: { xs: "flex", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                color: "#94A3B8",
                cursor: "pointer",
                flexShrink: 0,
                "&:hover": { bgcolor: "#F1F5F9", color: "#475569" },
              }}
            >
              <X size={11} />
            </Box>
          )}
        </Box>

        {/* Suggestions dropdown — only xs width/maxWidth tightened; sm unchanged */}
        {showPanel && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              right: 0,
              width: { xs: 260, sm: 320 },
              maxWidth: { xs: "calc(100vw - 32px)", sm: "88vw" },
              bgcolor: "white",
              border: "1px solid rgba(15,23,42,0.10)",
              borderTop: "1px solid rgba(15,23,42,0.06)",
              borderRadius: "0 0 12px 12px",
              boxShadow: "0 20px 40px -12px rgba(15,23,42,0.18)",
              overflow: "hidden",
              zIndex: 20,
            }}
          >
            {isFetching && !data ? (
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={2.75}>
                <CircularProgress size={14} thickness={5} sx={{ color: "#2563EB" }} />
                <Typography variant="body2" color="text.secondary" fontSize={13}>
                  Searching…
                </Typography>
              </Box>
            ) : !hasResults ? (
              <Box sx={{ textAlign: "center", py: 2.75, px: 2 }}>
                <Typography variant="body2" fontWeight={600} color="#334155" fontSize={13}>
                  No results for "{q}"
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4, fontSize: 11.5 }}>
                  Try a different keyword.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
                {groups.map(({ key, items, titleKey }, gi) => {
                  const meta = RESULT_META[key];
                  const Icon = meta.icon;
                  return (
                    <Box key={key} sx={{ borderTop: gi === 0 ? "none" : "1px solid rgba(15,23,42,0.05)" }}>
                      <Box sx={{ px: 1.75, pt: 1.2, pb: 0.4 }}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.07em",
                            textTransform: "uppercase",
                            color: "#94A3B8",
                          }}
                        >
                          {meta.label}s
                        </Typography>
                      </Box>
                      {items.map((item) => (
                        <Box
                          key={item.id}
                          component={Link}
                          to={meta.to()}
                          onClick={() => { setFocused(false); setQ(""); }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.1,
                            mx: 0.75,
                            px: 1,
                            py: 0.85,
                            borderRadius: "8px",
                            textDecoration: "none",
                            color: "inherit",
                            transition: "background-color 0.15s ease",
                            "&:hover": { bgcolor: "rgba(37,99,235,0.045)" },
                            "&:hover .search-arrow": { transform: "translateX(2px)", color: meta.color },
                          }}
                        >
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: "7px",
                              bgcolor: `${meta.color}14`,
                              color: meta.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Icon size={13} strokeWidth={2.25} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              fontSize={12.5}
                              sx={{ color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              {item[titleKey]}
                            </Typography>
                          </Box>
                          <ArrowRight
                            className="search-arrow"
                            size={13}
                            style={{ color: "#CBD5E1", flexShrink: 0, transition: "transform 0.15s ease, color 0.15s ease" }}
                          />
                        </Box>
                      ))}
                    </Box>
                  );
                })}

                <Box sx={{ px: 1.75, py: 0.7, borderTop: "1px solid rgba(15,23,42,0.05)", bgcolor: "#F8FAFF" }}>
                  <Typography sx={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 500 }}>
                    {totalCount} result{totalCount !== 1 ? "s" : ""} for "{q}"
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}